import { useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useFetcher } from "~/hooks/useFetcher";
import { ConversationView } from "~/pages/manifold-nextgen/components/conversations/ConversationRoute";
import { type PublishedApp, projectAppPath } from "~/services/apps";
import { getApiErrorMessage, isApiError } from "~/utils/apiClient";
import { useProjectContext } from "./useProjectContext";

// Router state that asks the session to open one of its apps full screen.
export interface OpenAppState {
  openArtifactAppId: string;
}

// The artifact app an edit session asks to open as it is entered.
export function openAppOnEntry(state: unknown): string | undefined {
  return typeof state === "object" &&
    state !== null &&
    "openArtifactAppId" in state &&
    typeof state.openArtifactAppId === "string"
    ? state.openArtifactAppId
    : undefined;
}

/**
 * An app published to the project, opened full screen in the caller's own session
 * with it. The page is that session's view, with the chat folded into a copilot,
 * so working in the app here or in a session is the same thing. The session joins
 * the caller's session list once they talk to the agent in it.
 */
export const PublishedAppPage = () => {
  const { projectId, project, appSession } = useProjectContext();
  const { appSlug = "" } = useParams<{ appSlug: string }>();
  const { data: app, error } = useFetcher<PublishedApp>(projectAppPath(projectId, appSlug));
  const session = appSession.session;
  const loadError = error ?? appSession.error;

  if (loadError) {
    return (
      <PageMessage>
        {isApiError(loadError) ? getApiErrorMessage(loadError) : "This app couldn't be opened."}
      </PageMessage>
    );
  }
  if (!app || !session) {
    return (
      <PageMessage>
        <Loader2
          className="size-6 animate-spin text-muted-foreground"
          role="status"
          aria-label="Opening app"
        />
      </PageMessage>
    );
  }
  return (
    <ConversationView
      key={session.id}
      conversationId={session.id}
      projectId={projectId}
      openApp={{ projectId: project.id, slug: app.slug }}
      defaultAgentId={app.agent_id}
      onTurnStart={() => {
        appSession.promote().catch((err) => console.error("Failed to list the app session", err));
      }}
    />
  );
};

const PageMessage = ({ children }: { children: React.ReactNode }) => (
  <div className="flex flex-1 items-center justify-center p-6 text-center text-sm text-muted-foreground">
    {children}
  </div>
);
